import React, { useState, useEffect, useRef } from "react";
import { Plus, Eye, Download, RefreshCw, X, Send, Check, Briefcase, Calendar, Phone, Mail, MapPin, FileText, Clock, User as UserIcon, Building, AlertCircle } from "lucide-react";
import "jspdf-autotable";
import { useNavigate } from "react-router-dom";
import DownloadModal from "../Components/Download/DownloadModal";
import DownloadService from "../Components/Download/DownloadService";
import { useLanguage } from "../StoreContext/LanguageContext";
import { useSignature } from "../DigitalSignature/UseSignatue";
import SignaturePad from "../DigitalSignature/SignaturePad";
import { jsPDF } from "jspdf";
import SendTrackingModal from "../CommonModel/SendTrackingModal";
import apiService from "../config";

// ==========================================
// REUSABLE DOCUMENT FORWARD MODAL COMPONENT
// ==========================================
const DocumentForwardModal = ({
  isOpen,
  onClose,
  title,
  users,
  loadingUsers,
  userLabel,
  targetRole,
  currentUserRole,
  ROLES,
  onSubmit,
  isSubmitting,
  translations,
  language,
  initialUser = "",
  initialRemarks = "Approved",
  uploadStatus,
  selectedSignDocument,
  setShowSignaturePad,
  setPdfForSigning,
  handleViewDocument
}) => {
  const [selectedUser, setSelectedUser] = useState(initialUser);
  const [remarks, setRemarks] = useState(initialRemarks);
  const [doc1, setDoc1] = useState(null);
  const [doc2, setDoc2] = useState(null);
  const [signingTarget, setSigningTarget] = useState(null); // Tracks 'doc1' or 'doc2'

  useEffect(() => {
    if (isOpen) {
      setSelectedUser(initialUser);
      setRemarks(initialRemarks);
    } else {
      setSelectedUser("");
      setRemarks("Approved");
      setDoc1(null);
      setDoc2(null);
      setSigningTarget(null);
    }
  }, [isOpen, initialUser, initialRemarks]);

  if (!isOpen) return null;

  // Determine if Document 2 is required based on role and target logic
  const requiresTwoDocs = 
    (currentUserRole === ROLES.SAMANVAY && (targetRole === "COLLECTOR" || targetRole === "LAO")) ||
    (currentUserRole === ROLES.COLLECTOR && targetRole === "SAMANVAY");

  const handleSubmit = () => {
    let finalDoc1 = doc1;
    let finalDoc2 = doc2;

    // Apply the signed document to whichever file was targeted for signing
    if (uploadStatus === "success" && selectedSignDocument) {
      if (signingTarget === 'doc1') finalDoc1 = selectedSignDocument;
      if (signingTarget === 'doc2') finalDoc2 = selectedSignDocument;
    }

    onSubmit({ selectedUser, remarks, doc1: finalDoc1, doc2: finalDoc2 });
  };

  const isSubmitDisabled = isSubmitting || !selectedUser || !doc1 
  // || (requiresTwoDocs && !doc2);

  // Helper function to render file inputs with View and Sign functionality
  const renderFileInput = (label, doc, setDoc, targetKey) => (
    <div className={targetKey === 'doc2' ? "mt-4 pt-4 border-t" : ""}>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label} *</label>
      <input 
        type="file" 
        onChange={(e) => {
          setDoc(e.target.files[0]);
          if (signingTarget === targetKey) setSigningTarget(null); // Reset signature if file changes
        }} 
        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" 
      />
      
      {doc && (
        <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-green-700 font-medium truncate max-w-[60%]">Selected: {doc.name}</p>
          </div>
          <div className="flex gap-2">
            {doc.type === "application/pdf" && (
              <button 
                type="button" 
                onClick={() => { 
                  setSigningTarget(targetKey); 
                  setPdfForSigning(doc); 
                  setShowSignaturePad(true); 
                }} 
                disabled={uploadStatus === "uploading"} 
                className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400"
              >
                {uploadStatus === "uploading" && signingTarget === targetKey ? "Signing..." : "Sign Doc"}
              </button>
            )}
            <button 
              type="button" 
              onClick={() => {
                const docToView = (signingTarget === targetKey && uploadStatus === "success" && selectedSignDocument) 
                  ? selectedSignDocument 
                  : doc;
                handleViewDocument(docToView);
              }} 
              className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              View
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{userLabel} *</label>
              <select 
                value={selectedUser} 
                onChange={(e) => setSelectedUser(e.target.value)} 
                disabled={loadingUsers} 
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{loadingUsers ? translations[language]?.LoadingUsers || "Loading users..." : translations[language]?.SelectUser || "Select User"}</option>
                {users?.map((user) => (
                  <option key={user.id || user.usr_id} value={user.id || user.usr_id}>
                    {user.full_name || user.user_name} ({user.usr_role || user.user_role || "Officer"})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{translations[language]?.Remarks || "Remarks"}</label>
              <textarea 
                value={remarks} 
                onChange={(e) => setRemarks(e.target.value)} 
                rows={3} 
                className="w-full p-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                placeholder="Enter remarks..." 
              />
            </div>

            {/* Render Document 1 Input with View/Sign controls */}
            {renderFileInput("Upload Document 1", doc1, setDoc1, 'doc1')}

            {/* Render Document 2 Input with View/Sign controls (Conditional) */}
            {requiresTwoDocs && renderFileInput("Upload Document 2", doc2, setDoc2, 'doc2')}
          </div>

          <div className="flex justify-end gap-3 mt-3 pt-2">
            <button 
              onClick={onClose} 
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50" 
              disabled={isSubmitting}
            >
             {translations[language].Cancel}
            </button>
            <button 
              onClick={handleSubmit} 
              disabled={isSubmitDisabled} 
              className={`px-6 py-2 rounded-lg text-white font-medium shadow-md transition-colors flex items-center ${
                isSubmitDisabled ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
              }`}
            >
              {isSubmitting ? translations[language].Sending : translations[language].Send}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================
export default function ObjectionList({
  onUpdated,
  setSubComponent,
  modalParams,
  shouldRestoreModal,
  onNavigateWithModal,
  setShouldRestoreModal,
}) {
  const { language, translations } = useLanguage();
  const [activeTab, setActiveTab] = useState("all");

  const [savingId, setSavingId] = useState(null);
  const [activeScreen, setActiveScreen] = useState("list");
  const [selectedObjection, setSelectedObjection] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });
  const [sortConfig, setSortConfig] = useState({ key: "date", direction: "descending" });
  
  const [villages, setVillages] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const downloadRef = useRef(null);
  const filterRef = useRef(null);
  const navigate = useNavigate();

  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [objections, setObjections] = useState([]);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsData, setDetailsData] = useState(null);

  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [currentProposalId, setCurrentProposalId] = useState(null);
  const [currentProjectName, setCurrentProjectName] = useState(null);

  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  
  const [users, setUsers] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [selectedDecisionDate, setSelectedDecisionDate] = useState("");

  // ==========================================
  // UNIFIED MODAL STATE
  // ==========================================
  const [activeModal, setActiveModal] = useState(null); // 'SAMANVAY' | 'COLLECTOR' | 'LAO' | null
  const [isSubmittingTask, setIsSubmittingTask] = useState(false);
  const [restoredModalData, setRestoredModalData] = useState({ user: "", remarks: "Approved" });

  const [usersSamanvay, setusersSamanvay] = useState([]);
  const [loadingusersSamanvay, setLoadingusersSamanvay] = useState(false);
  
  const [usersCollectorAll, setusersCollectorAll] = useState([]);
  const [loadingusersCollector, setLoadingusersCollector] = useState(false);

  const [usersLAO, setusersLAO] = useState([]);
  const [loadingusersLAO, setLoadingusersLAO] = useState(false);

  const [sentStatus, setSentStatus] = useState({ samanvay: false, lao: false, collector: false });

  // Digital signature hooks and state
  const { uploadStatus, uploadError, selectedSignDocument, resetSignatureState, signDocument } = useSignature();
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [pdfForSigning, setPdfForSigning] = useState(null);
  
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({ title: "", message: "", onConfirm: null, onCancel: null, type: "warning" });

  const showConfirm = (title, message, onConfirm, onCancel = null, type = "warning") => {
    setConfirmConfig({ title, message, onConfirm, onCancel, type });
    setShowConfirmModal(true);
  };

  const [showDocumentViewer, setShowDocumentViewer] = useState(false); 
  const [fileUrlToView, setFileUrlToView] = useState("");
  const [isPDFToView, setIsPDFToView] = useState(false);

  const [decision, setDecision] = useState("");
  const [isProcessingDecision, setIsProcessingDecision] = useState(false);
  const [officerDecision, setOfficerDecision] = useState("");

  const [isSamanvaySent, setIsSamanvaySent] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [proposalId, setProposalId] = useState(null);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [punarvarshanData, setPunarvarshanData] = useState([]);
  const [hasUserEditedSection1, setHasUserEditedSection1] = useState(false);
  const [hasUserEditedTip, setHasUserEditedTip] = useState(false);
  const [formData, setFormData] = useState({});
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: "", message: "", type: "success" });
  
  const [landData, setLandData] = useState([]);
  const [punarvarshanGroupedData, setPunarvarshanGroupedData] = useState([]);
  const [collectorEditCompletedbuttonDisabled, setCollectorEditCompletedbuttonDisabled] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [groupedData, setGroupedData] = useState([]);
  const [status, setStatus] = useState("");

  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingRole, setTrackingRole] = useState("");

  const ROLES = { COLLECTOR: 1, LAO: 3, SAMANVAY: 6, PROJECT_INCHARGE: 18, DATA_ENTRY: 14 };

  const [userRole, setUserRole] = useState(() => {
    const userData = localStorage.getItem("userData");
    if (userData) {
      const user = JSON.parse(userData);
      return user.user_role || null;
    }
    return null;
  });
  const[isSamanvay , setIsSamanvay] = useState(false)
  const[isCollector , setIsCollector] = useState(false)
  const[isSamanvayFinal , setIsSamanvayFinal] = useState(false)
  const[isLAO , setIsLAO] = useState(false)
 useEffect(() => {
   const fetchButtonState = async () => {
     try {
       if (!currentProjectId || !currentProposalId) return;
 
       const res = await apiService.getTrackingwiseSEC15Button(currentProjectId,currentProposalId);
         console.log("API response:", res);
         setIsSamanvay(res?.stages?.ObjectionSamanvay?.complete)
         setIsCollector(res?.stages?.ObjectionCollector?.complete)
         setIsSamanvayFinal(res?.stages?.ObjectionSamanvay?.complete)
         setIsLAO(res?.stages?.ObjectionLAO?.complete)
 
       // Correct mapping
       // setButtonDisabled(!res?.isEditable);
 
     } catch (error) {
       console.error("Error fetching button state:", error);
       // setButtonDisabled(true); // fail-safe
     }
   };  fetchButtonState();
 }, [currentProjectId, currentProposalId]);

  useEffect(() => {
    allApi();
  }, [currentProjectId && currentProposalId]);

  const allApi = () => {
    if (currentProjectId && currentProposalId) {
      fetchProposalData();
      fetchLandHoldingData();
      fetchOwnerData();
      fetchusersLAOList();
      fetchPunarvarshanData();
      fetchButtonStatus();
    }
  };

  useEffect(() => {
    if (activeModal === 'COLLECTOR') fetchUsersAllCollector();
    if (activeModal === 'SAMANVAY') fetchusersSamanvay();
    if (activeModal === 'LAO') fetchusersLAOList();
  }, [activeModal]);

  const handleViewDetails = async (type, obj) => {
    try {
      setIsLoading(true);
      let response;
      let numericId = obj.id.replace(/^[G|P]/, "");

      if (type === "Private") {
        response = await apiService.getPrivateObjectionById(numericId);
      } else {
        response = await apiService.getGeneralObjectionById(numericId);
      }

      const objectionData = response?.data || response;
      objectionData.id = obj.id; 
      objectionData.status = obj.status; 
      objectionData.type = obj.type; 

      setDetailsData(objectionData);
      setShowDetailsModal(true);

      setNotification({
        show: true,
        type: "success",
        message: "Objection details loaded successfully",
      });
    } catch (error) {
      console.error("Error fetching objection details:", error);
      setNotification({
        show: true,
        type: "error",
        message: "Failed to load objection details",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDocument = (urlOrFile) => {
    if (!urlOrFile) return;
    let url;
    let isPdf;

    if (urlOrFile instanceof File) {
      url = URL.createObjectURL(urlOrFile);
      isPdf = urlOrFile.type === "application/pdf";
    } else {
      url = urlOrFile;
      isPdf = urlOrFile.toLowerCase().includes(".pdf");
    }

    setFileUrlToView(url);
    setIsPDFToView(isPdf);
    setShowDocumentViewer(true);
  };

  const fetchUsersAllCollector = async () => {
    const role = 1;
    setLoadingusersCollector(true);
    try {
      const response = await apiService.getAllAssignUserCollector(role, currentProjectId, currentProposalId);
      if (response.status === 200 && response.data) {
        setusersCollectorAll(Array.isArray(response.data) ? response.data : []);
      } else {
        setusersCollectorAll([]);
      }
    } catch (error) {
      setusersCollectorAll([]);
    } finally {
      setLoadingusersCollector(false);
    }
  };

  const fetchusersSamanvay = async () => {
    const role = 6;
    setLoadingusersSamanvay(true);
    try {
      const response = await apiService.getAllAssignUserRoleSamanvay(role, currentProjectId, currentProposalId);
      if (response.status === 200 && response.data) {
        setusersSamanvay(Array.isArray(response.data) ? response.data : []);
      } else {
        setusersSamanvay([]);
      }
    } catch (error) {
      setusersSamanvay([]);
    } finally {
      setLoadingusersSamanvay(false);
    }
  };

  const fetchusersLAOList = async () => {
    const role = 3;
    const selectedProject = JSON.parse(localStorage.getItem("selectedProject") || "{}");
    const prjId = selectedProject?.id || selectedProject?.project_id || currentProjectId;
    const selectedProposal = JSON.parse(localStorage.getItem("selectedProposal") || "{}");
    const propId = selectedProposal?.acquisition_proposal_id || currentProposalId;
    setLoadingusersLAO(true);
    try {
      const response = await apiService.getAssignLAO(role, prjId, propId);
      if (response.status === 200 && response.data) {
        setusersLAO(Array.isArray(response.data) ? response.data : []);
      } else {
        setusersLAO([]);
      }
    } catch (error) {
      setusersLAO([]);
    } finally {
      setLoadingusersLAO(false);
    }
  };

  const fetchLandHoldingData = async () => {
    if (!projectId || !proposalId || projectId <= 0 || proposalId <= 0) return;
    const locationParams = { proposalId };
    try {
      setLoadingRecords(true);
      const result = await apiService.getLandHolding(projectId, locationParams);
      if (result.status === 200 && Array.isArray(result.proposalData) && result.proposalData.length > 0) {
        const item = result.proposalData[0];
        const newFormData = {};
        Object.keys(formData).forEach((key) => {
          newFormData[key] = item[key] !== undefined && item[key] !== null ? item[key] : "";
        });
        setFormData(newFormData);

        if (item.section_19_content && item.section_19_content.trim() !== "" && item.section_19_content !== "N/A" && item.section_19_content !== "null" && item.section_19_content !== "undefined") {
          setHasUserEditedSection1(true);
        }
        if (item.section_19_tip && item.section_19_tip.trim() !== "" && item.section_19_tip !== "N/A" && item.section_19_tip !== "null" && item.section_19_tip !== "undefined") {
          setHasUserEditedTip(true);
        }
      }
    } catch (error) {
      console.error("Error fetching Prathmik Adhisuchna data:", error);
    } finally {
      setLoadingRecords(false);
    }
  };

  const fetchProposalData = async () => {
    try {
      const response = await apiService.get("getProposal", { 
        projectId: currentProjectId || projectId, 
        proposalId: currentProposalId || proposalId 
      });
      const dtvArray = response?.data?.dtv || response?.dtv;
      if (Array.isArray(dtvArray)) {
        setVillages(dtvArray);
      }
    } catch (err) {
      console.error("Error fetching proposal data:", err);
    }
  };

  const fetchOwnerData = async () => {
    if (!projectId || !proposalId || projectId <= 0 || proposalId <= 0) return;
    try {
      setLoadingRecords(true);
      const result = await apiService.get("getAllOwner", { projectId, proposalId, isActive: 1 });
      if (result.status === 200 && Array.isArray(result.landAcquisition)) {
        const grouped = {};
        const groupedLand = {};

        result.landAcquisition.forEach((item) => {
          if (Array.isArray(item.owners) && item.owners.length > 0) {
            const key = `${item.dist_name}-${item.taluka_name}-${item.village_name}`;
            if (!grouped[key]) grouped[key] = [];
            item.owners.forEach((owner) => {
              grouped[key].push({
                survey_no: owner.survey_no,
                aqr_area: owner.owner_aqr_area,
                total_area: parseFloat(item.total_area || 0),
                owner_irrigable_area: owner.owner_irrigable_area || 0,
                owner_non_irrigable_area: owner.owner_non_irrigable_area || 0,
              });
            });
          }

          const landKey = `${item.dist_name}-${item.taluka_name}-${item.village_name}`;
          if (!groupedLand[landKey]) groupedLand[landKey] = [];
          groupedLand[landKey].push({
            survey_no: item.survey_no,
            irrigable_area: item.irrigable_area,
            non_irrigable_area: item.non_irrigable_area,
            total_area: parseFloat(item.total_area || 0),
          });
        });

        const groupedArray = Object.entries(grouped).map(([key, records]) => {
          const [dist, taluka, village] = key.split("-");
          const ownerTotalIrrigable = records.reduce((sum, r) => sum + parseFloat(r.owner_irrigable_area || 0), 0);
          const ownerTotalNonIrrigable = records.reduce((sum, r) => sum + parseFloat(r.owner_non_irrigable_area || 0), 0);
          const total = records.reduce((sum, r) => sum + parseFloat(r.aqr_area || 0), 0);
          return { dist, taluka, village, records, total, ownerTotalIrrigable, ownerTotalNonIrrigable };
        });

        const holderData = Object.entries(groupedLand).map(([key, records]) => {
          const [dist, taluka, village] = key.split("-");
          const irrigableTotal = records.reduce((sum, r) => sum + parseFloat(r.irrigable_area || 0), 0);
          const nonIrrigableTotal = records.reduce((sum, r) => sum + parseFloat(r.non_irrigable_area || 0), 0);
          const finalTotal = records.reduce((sum, r) => sum + parseFloat(r.total_area || 0), 0);
          return { dist, taluka, village, records, irrigableTotal, nonIrrigableTotal, finalTotal };
        });

        setGroupedData(groupedArray);
        setLandData(holderData);
      }
    } catch (error) {
      console.error("Error fetching Prathmik Adhisuchna data:", error);
    } finally {
      setLoadingRecords(false);
    }
  };

  const fetchPunarvarshanData = async () => {
    if (!projectId || !proposalId) return;
    try {
      setLoadingRecords(true);
      const result = await apiService.get("getPunarvarshan", { projectId, proposalId });
      if (result.status === 200 && Array.isArray(result.data) && result.data.length > 0) {
        setPunarvarshanData(
          result.data.map((item) => ({
            id: item.punarvarshan_id,
            survey_no: item.survey_no,
            aquire_area: item.aquire_area,
            dist_name: item.dist_name,
            taluka_name: item.taluka_name,
            village_name: item.village_name,
          }))
        );
        const grouped = {};
        result.data.forEach((item) => {
          const key = `${item.dist_name}-${item.taluka_name}-${item.village_name}`;
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push({ id: item.punarvarshan_id, survey_no: item.survey_no, aquire_area: item.aquire_area });
        });

        const groupedArray = Object.entries(grouped).map(([key, records]) => {
          const [dist, taluka, village] = key.split("-");
          const total = records.reduce((sum, r) => sum + parseFloat(r.aquire_area || 0), 0);
          return { dist, taluka, village, records, total };
        });

        setPunarvarshanGroupedData(groupedArray);
      }
    } catch (error) {
      console.error("Error fetching Punarvarshan data:", error);
    } finally {
      setLoadingRecords(false);
    }
  };

  const fetchButtonStatus = async () => {
    try {
      if (!projectId || !proposalId) return;
      let response;
      response = await apiService.getfetchLAOPublishSection19Button(projectId, proposalId);
      setCollectorEditCompletedbuttonDisabled(!response.buttonDisable);

      switch (userRole) {
        case ROLES.LAO:
          response = await apiService.getfetchSamanvayButtonSection19(projectId, proposalId);
          break;
        case ROLES.SAMANVAY:
          response = await apiService.getfetchCollectorButtonSection19(projectId, proposalId);
          break;
        case ROLES.COLLECTOR:
          response = await apiService.getfetchLAOPublishSection19Button(projectId, proposalId);
          break;
        default:
          return;
      }
      setButtonDisabled(!response.buttonDisable);
    } catch (error) {
      console.error("Error fetching button state:", error);
    }
  };

  const closeDocumentViewer = () => {
    setShowDocumentViewer(false);
    if (fileUrlToView.startsWith("blob:")) URL.revokeObjectURL(fileUrlToView);
    setFileUrlToView("");
    setIsPDFToView(false);
  };

  // ==========================================
  // UNIFIED SUBMIT HANDLER
  // ==========================================
  const handleUnifiedSubmit = async ({ selectedUser, remarks, doc1, doc2 }) => {
    const pId = currentProjectId || JSON.parse(localStorage.getItem("selectedProject") || "{}").id;
    const pID2 = currentProposalId || localStorage.getItem("currentProposalId") || JSON.parse(localStorage.getItem("selectedProposal") || "{}").id;

    if (!pId || !pID2) {
      setNotification({ show: true, type: "error", message: "Project ID and Proposal ID are required." });
      return;
    }

    setIsSubmittingTask(true);
    try {
      const formData = new FormData();
      formData.append("project_id", pId);
      formData.append("proposal_id", pID2);
      formData.append("remarks", remarks || "Approved");
      formData.append("usr_id", selectedUser);

      if (doc1) formData.append("document", doc1);
      if (doc2) formData.append("document2", doc2);

      let response;
      if (activeModal === 'COLLECTOR') {
        response = await apiService.postTrackingCollectorObjection(formData);
      } else if (activeModal === 'LAO') {
        response = await apiService.postTrackingLAOObjection(formData);
      } else if (activeModal === 'SAMANVAY') {
        if (userRole === ROLES.LAO) {
          response = await apiService.postTrackingSamanvayObjection(formData);
        } else if (userRole === ROLES.COLLECTOR) {
          response = await apiService.postTrackingSamanvayFinalObjection(formData);
        }
      }

      if (response && (response.status === 200 || response.status === 201)) {
        setNotification({ show: true, type: "success", message: `Successfully sent to ${activeModal} Officer!` });
        
        if (activeModal === 'SAMANVAY') {
          setSentStatus((prev) => ({ ...prev, samanvay: true }));
          setIsSamanvaySent(true);
          resetSignatureState();
          setButtonDisabled(true);
        }

        await allApi();
        fetchButtonStatus();
        setActiveModal(null);
      } else {
        throw new Error(`Unexpected response status: ${response?.status}`);
      }
    } catch (error) {
      console.error("Submission Error:", error);
      setNotification({ show: true, type: "error", message: `Failed to send to ${activeModal} Officer. Please try again.` });
    } finally {
      setIsSubmittingTask(false);
    }
  };

  const getModalConfig = () => {
    switch (activeModal) {
      case 'SAMANVAY':
        return {
          title: translations[language]?.SendToSamanvayOfficer || "Send to Samanvay Officer",
          userLabel: translations[language]?.selectUserSamanvayOfficer || "Select Samanvay Officer",
          users: usersSamanvay,
          loadingUsers: loadingusersSamanvay
        };
      case 'COLLECTOR':
        return {
          title: "Send to Collector",
          userLabel: translations[language]?.SelectUserCollector || "Select Collector",
          users: usersCollectorAll,
          loadingUsers: loadingusersCollector
        };
      case 'LAO':
        return {
          title: "Send to LAO",
          userLabel: translations[language]?.SelectUserLAO || "Select LAO",
          users: usersLAO,
          loadingUsers: loadingusersLAO
        };
      default:
        return {};
    }
  };
  const [pageActive , setPageActive] = useState()

  useEffect(() => {
    const fetchSamanvayButtonStatus = async () => {
      try {
        if (currentProjectId && currentProposalId) {
          const res = await apiService.getSendToSamanvayObjectionButton(currentProjectId, currentProposalId);
          const Buttonres = await apiService.getSendbutton(currentProjectId, currentProposalId,"LAOV");
          console.log("Button Response", Buttonres);
          setPageActive(!Buttonres?.buttonDisable)
          
          setIsSamanvaySent(!res.buttonDisable);
        }
      } catch (err) {
        setIsSamanvaySent(false);
      }
    };
    fetchSamanvayButtonStatus();
  }, [currentProjectId, currentProposalId]);

  const getFieldLabel = (key) => {
    const fieldLabels = {
      objector_name: <span>{translations[language].objectorName}</span>,
      objector_address: <span>{translations[language].objectorAddress}</span>,
      objector_contact: "Contact Number",
      objector_email: "Email Address",
      objection_date: <span>{translations[language].objectionDate}</span>,
      objection_summary: "Objection Summary",
      objection_details: <span>{translations[language].objectionDetails}</span>,
      objection_status: <span>{translations[language].CurrentStatus}</span>,
      project_id: <span>{translations[language].projectId}</span>,
      project_name: "Project Name",
      submission_date: "Submission Date",
      last_updated: "Last Updated",
      priority_level: "Priority Level",
      category: "Category",
      affected_area: "Affected Area",
      compensation_requested: "Compensation Requested",
      resolution_required: "Resolution Required",
      documentation_provided: "Documentation Provided",
      supporting_documents: "Supporting Documents",
      document_references: "Document References",
      identity_proof_no: "Identity Proof Number",
      identity_proof_type: "Identity Proof Type",
      objection_id: "Objection ID",
      objector_type: "Objector Type",
      affected_survey_no: <span>{translations[language].affectedSurveyNo}</span>,
      village_name: "Affected Village",
      taluka_name: "Affected Taluka",
      dist_name: "Affected District",
      objection_category: "Objection Category",
      resolution_details: "Resolution Details",
      resolution_date: "Resolution Date",
      hearing_date: "Hearing Date",
      hearing_venue: "Hearing Venue",
      hearing_officer: "Hearing Officer",
      hearing_designation: "Hearing Designation",
      case_no: <span>{translations[language].caseNumber}</span>,
      identityproof: "Identity Proof",
      identity_proof: "Identity Proof",
    };

    if (fieldLabels[key]) return fieldLabels[key];
    const lowerKey = key.toLowerCase();
    if (fieldLabels[lowerKey]) return fieldLabels[lowerKey];
    const snakeCase = key.replace(/([A-Z])/g, "_$1").toLowerCase();
    if (fieldLabels[snakeCase]) return fieldLabels[snakeCase];
    return key.replace(/_/g, " ").replace(/([A-Z])/g, " $1").trim().split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
  };

  const getFieldIcon = (key) => {
    const lowerKey = key.toLowerCase();
    if (lowerKey.includes("name")) return <UserIcon className="h-4 w-4" />;
    if (lowerKey.includes("address") || lowerKey.includes("location")) return <MapPin className="h-4 w-4" />;
    if (lowerKey.includes("contact") || lowerKey.includes("phone")) return <Phone className="h-4 w-4" />;
    if (lowerKey.includes("email")) return <Mail className="h-4 w-4" />;
    if (lowerKey.includes("date") || lowerKey.includes("time")) return <Calendar className="h-4 w-4" />;
    if (lowerKey.includes("status")) return <AlertCircle className="h-4 w-4" />;
    if (lowerKey.includes("project")) return <Building className="h-4 w-4" />;
    if (lowerKey.includes("summary") || lowerKey.includes("details") || lowerKey.includes("description")) return <FileText className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  const formatFieldValue = (key, value) => {
    if (typeof value === "string" && (key.includes("date") || key.includes("time")) && !isNaN(Date.parse(value))) {
      return new Date(value).toLocaleString("en-GB", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: key.includes("time") ? "2-digit" : undefined,
        minute: key.includes("time") ? "2-digit" : undefined,
      }).replace(",", "");
    }
    if (typeof value === "object" && value !== null) return JSON.stringify(value, null, 2);
    return String(value || "No Data available");
  };

  const getStatusStyling = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower.includes("pending")) return "bg-yellow-100 text-yellow-800 border-yellow-200";
    if (statusLower.includes("approved") || statusLower.includes("completed")) return "bg-green-100 text-green-800 border-green-200";
    if (statusLower.includes("rejected") || statusLower.includes("denied")) return "bg-red-100 text-red-800 border-red-200";
    if (statusLower.includes("in progress") || statusLower.includes("scheduled")) return "bg-blue-100 text-blue-800 border-blue-200";
    return "bg-gray-100 text-gray-800 border-gray-200";
  };

  const isImageField = (key, value) => {
    const lowerKey = key.toLowerCase();
    const stringValue = String(value || "").toLowerCase();
    return ((lowerKey.includes("proof") || lowerKey.includes("document") || lowerKey.includes("attachment")) &&
      (stringValue.includes("http") || stringValue.includes(".jpg") || stringValue.includes(".jpeg") || stringValue.includes(".png") || stringValue.includes(".pdf")));
  };

  const handleViewImage = (imageUrl, fieldName) => {
    setSelectedImage({ url: imageUrl, title: fieldName });
    setShowImageModal(true);
  };

  const renderImageModal = () => {
    if (!showImageModal || !selectedImage) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{selectedImage.title}</h3>
                  <p className="text-blue-100 text-sm">Document Preview</p>
                </div>
              </div>
              <button onClick={() => setShowImageModal(false)} className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white hover:bg-opacity-10 rounded-lg">
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
          <div className="p-6 max-h-[calc(90vh-120px)] overflow-auto">
            <div className="flex justify-center">
              {selectedImage.url.toLowerCase().includes(".pdf") ? (
                <iframe src={selectedImage.url} className="w-full h-96 border rounded-lg" title="PDF Viewer" />
              ) : (
                <img src={selectedImage.url} alt={selectedImage.title} className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                  onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "block"; }} />
              )}
              <div className="hidden text-center text-gray-500 p-8 border-2 border-dashed border-gray-300 rounded-lg">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                <p>Unable to load image</p>
                <a href={selectedImage.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">Open in new tab</a>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <a href={selectedImage.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline flex items-center">
                Open in new tab
              </a>
              <button onClick={() => setShowImageModal(false)} className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDetailsModal = () => {
    if (!showDetailsModal || !detailsData) return null;

    const excludedFields = ["supporting_documents", "objector_email", "id", "type", "objection_other", "document_references", "identity_proof_no", "identity_proof_type", "identityProof", "village_name", "taluka_name", "dist_name", "objector_contact", "objection_category", "objector_type", "objection_summary", "objection_no", "objection_id", "supportive_document", "hearing_outcome", "is_active", "cre_usr_dt", "last_upd_id", "last_upd_dt", "otp", "cre_usr_id", "hearing_required", "review_comments", "resolution_details", "resolution_date", "hearing_date", "hearing_venue", "hearing_officer", "hearing_designation"];
    
    const handleSave = async (objectionData) => {
      if (!selectedDecisionDate || !decision || !officerDecision) {
        setNotification({ show: true, type: "error", message: "Please select both decision and date" });
        return;
      }
      setIsProcessingDecision(true);
      try {
        const numericId = objectionData.id?.replace(/^[GP]/, "") || objectionData.objection_id || objectionData.pobjection_id;
        const isPrivateObjection = objectionData.type === "Private" || objectionData.pobjection_id;
        const updateData = { OBJECTION_STATUS: decision, hearing_date: selectedDecisionDate, hearing_outcome: officerDecision };

        if (!isPrivateObjection) {
          await apiService.patchUpdateGeneralObjection(numericId, updateData);
        }

        const updatedObjections = objections.map((obj) => {
          const objNumeric = obj.id.replace(/^[GP]/, "");
          return objNumeric === numericId ? { ...obj, status: decision, hearing_date: selectedDecisionDate, hearing_outcome: officerDecision } : obj;
        });
        setObjections(updatedObjections);

        setDetailsData({ ...objectionData, OBJECTION_STATUS: decision, objection_status: decision, hearing_date: selectedDecisionDate, hearing_outcome: officerDecision });
        setNotification({ show: true, type: "success", message: "Status updated successfully" });
        setDecision(""); setSelectedDecisionDate(""); setOfficerDecision("");
      } catch (error) {
        setNotification({ show: true, type: "error", message: "Failed to update status" });
      } finally {
        setIsProcessingDecision(false);
      }
    };

    const bottomFields = ["supporting_documents", "document_references"];
    const importantFields = ["objector_name", "objection_status", "objection_date", "objector_address", "objection_details"];
    
    const allFilteredData = Object.entries(detailsData).filter(([key, value]) => !excludedFields.includes(key.toLowerCase()));
    const prominentFields = allFilteredData.filter(([key]) => importantFields.includes(key.toLowerCase()));
    const bottomSectionFields = allFilteredData.filter(([key]) => {
      const lowerKey = key.toLowerCase();
      return bottomFields.includes(lowerKey) || lowerKey.includes("supporting") || lowerKey.includes("document_ref");
    });
    const regularFields = allFilteredData.filter(([key]) => {
      const lowerKey = key.toLowerCase();
      const originalKey = key;
      return !importantFields.includes(lowerKey) && !bottomFields.includes(lowerKey) && originalKey !== "identity Proof" && originalKey !== "identityProof" && !(lowerKey.includes("identity") && (lowerKey.includes("proof") || lowerKey.includes("document"))) && !lowerKey.includes("supporting") && !lowerKey.includes("document_ref") && lowerKey !== "affected_village_old" && lowerKey !== "affected_taluka_old" && lowerKey !== "affected_district_old";
    });

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-8 py-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div className="bg-white bg-opacity-20 p-3 rounded-lg"><FileText className="h-8 w-8 text-white" /></div>
                <div>
                  <h3 className="text-2xl font-bold text-white">{translations[language].objectionDetails}</h3>
                  <p className="text-blue-100 text-sm mt-1">Complete objection information and details</p>
                </div>
              </div>
              <button onClick={() => setShowDetailsModal(false)} className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white hover:bg-opacity-10 rounded-lg">
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto max-h-[calc(95vh-200px)]">
            {prominentFields.length > 0 && (
              <div className="p-8 bg-gray-50 border-b border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center"><AlertCircle className="h-5 w-5 mr-2 text-blue-600" /> Key Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {prominentFields.map(([key, value]) => (
                    <div key={key} className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="text-blue-600">{getFieldIcon(key)}</div>
                        <span className="text-sm font-medium text-gray-600">{getFieldLabel(key)}</span>
                      </div>
                      <div className="mt-2">
                        {key.toLowerCase().includes("status") ? (
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusStyling(value)}`}>{formatFieldValue(key, value)}</span>
                        ) : (
                          <p className="text-gray-900 font-medium">{formatFieldValue(key, value)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {(detailsData?.objection_status?.toLowerCase() === "pending" || detailsData?.OBJECTION_STATUS?.toLowerCase() === "pending") && userRole === 3 ? (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">निर्णय निवडा *</label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" value={decision} onChange={(e) => setDecision(e.target.value)} disabled={isProcessingDecision}>
                        <option value="">-- निर्णय निवडा * --</option>
                        <option value="Completed">पूर्ण झाले म्हणून चिन्हांकित करा</option>
                        <option value="Rejected">अपूर्ण म्हणून चिन्हांकित करा</option>
                      </select>
                    </div>
                    {decision && (
                      <div className="animate-fadeIn">
                        <label className="block text-sm font-medium text-gray-700 mb-2">सुनावणी *</label>
                        <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" value={selectedDecisionDate} onChange={(e) => setSelectedDecisionDate(e.target.value)} disabled={isProcessingDecision} min={new Date().toISOString().split("T")[0]} />
                      </div>
                    )}
                    {decision && selectedDecisionDate && (
                      <div className="animate-fadeIn">
                        <label className="block text-sm font-medium text-gray-700 mb-2">भूसंपादन अधिकाऱ्याचा निर्णय *</label>
                        <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none" rows="3" value={officerDecision} onChange={(e) => setOfficerDecision(e.target.value)} disabled={isProcessingDecision} placeholder="Enter officer's decision..." />
                      </div>
                    )}
                    {decision && selectedDecisionDate && officerDecision && (
                      <div className="animate-fadeIn">
                        <button onClick={() => handleSave(detailsData)} disabled={isProcessingDecision || !decision || !selectedDecisionDate || !officerDecision} className="w-full px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white font-medium rounded-lg hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed flex items-center justify-center shadow-lg">
                          {isProcessingDecision ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Processing...</> : <><Check className="h-4 w-4 mr-2" /> Save Decision</>}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-green-100 rounded-lg"><Check className="h-5 w-5 text-green-600" /></div>
                          <div>
                            <h6 className="font-medium text-gray-900">सध्याची स्थिती: {detailsData?.objection_status || detailsData?.OBJECTION_STATUS}</h6>
                            {detailsData?.hearing_date && <p className="text-sm text-gray-500">तारीख : {new Date(detailsData.hearing_date).toLocaleDateString("en-GB").replace(/\//g, "-")}</p>}
                            {detailsData?.hearing_outcome && <p className="text-sm text-gray-600 mt-1">भूसंपादन अधिकाऱ्याचा निर्णय: {detailsData.hearing_outcome}</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {bottomSectionFields.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h5 className="text-md font-semibold text-gray-800 mb-4 flex items-center"><FileText className="h-4 w-4 mr-2 text-blue-600" /> <span>{translations[language].SupportingDocumentsReferences}</span></h5>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {bottomSectionFields.map(([key, value]) => (
                        <div key={key} className="bg-white rounded-xl p-5 border border-gray-200 hover:border-gray-300 transition-colors shadow-sm hover:shadow-md">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 mt-1"><div className="p-2 bg-blue-50 rounded-lg text-blue-600">{getFieldIcon(key)}</div></div>
                            <div className="flex-1 min-w-0">
                              <label className="block text-sm font-semibold text-gray-700 mb-2">{getFieldLabel(key)}</label>
                              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                {isImageField(key, value) ? (
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2"><FileText className="h-4 w-4 text-gray-500" /><span className="text-sm text-gray-600">Document attached</span></div>
                                    <button onClick={() => handleViewImage(value, getFieldLabel(key))} className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors duration-200 flex items-center space-x-1"><span>View</span></button>
                                  </div>
                                ) : (
                                  <p className="text-gray-800 font-medium">{formatFieldValue(key, value)}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-gray-50 px-8 py-6 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500"><Clock className="h-4 w-4 inline mr-1" /> Last updated: {new Date().toLocaleDateString()}</div>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowDetailsModal(false)} className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2">{translations[language].Close}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const getProjectInfo = () => {
      const currentProjectData = localStorage.getItem("currentProjectData");
      if (currentProjectData) {
        try {
          const projectData = JSON.parse(currentProjectData);
          return { id: projectData.project_id || projectData.id, name: projectData.project_name || projectData.name };
        } catch (e) {}
      }
      const selectedProject = localStorage.getItem("selectedProject");
      if (selectedProject) {
        try {
          const project = JSON.parse(selectedProject);
          return { id: project.project_id || project.id, name: project.project_name || project.name };
        } catch (e) {}
      }
      const userData = localStorage.getItem("userData");
      if (userData) {
        try {
          const user = JSON.parse(userData);
          return { id: user.data?.project_id || user.project_id, name: user.data?.project_name || user.project_name || "Unknown Project" };
        } catch (e) {}
      }
      return { id: null, name: null };
    };

    const projectInfo = getProjectInfo();
    setCurrentProjectId(projectInfo.id);
    setCurrentProjectName(projectInfo.name);
    const currentProposalId = localStorage.getItem("currentProposalId");
    if (currentProposalId) setCurrentProposalId(currentProposalId);
  }, []);

  useEffect(() => {
    if (!currentProjectId) return;

    const fetchAllObjections = async () => {
      try {
        setIsLoading(true);
        const currentProposalId = localStorage.getItem("currentProposalId");
        if (!currentProposalId) {
          setNotification({ show: true, type: "error", message: "Proposal ID missing. Please select a project first." });
          return;
        }

        let generalRes = null;
        let privateRes = null;

        try {
          generalRes = await apiService.getAllGeneralObjections(currentProjectId, currentProposalId);
        } catch (error) { generalRes = { data: [] }; }

        try {
          privateRes = await apiService.getAllPrivateObjections(currentProjectId, currentProposalId);
        } catch (error) { privateRes = { data: [] }; }

        const safeGeneralData = Array.isArray(generalRes?.data || generalRes) ? (generalRes?.data || generalRes) : [];
        const safePrivateData = Array.isArray(privateRes?.data || privateRes) ? (privateRes?.data || privateRes) : [];

        const general = safeGeneralData.map((item, index) => ({
          id: `G${item.objection_id || index}`,
          type: "General",
          name: item.objector_name || "Unknown",
          date: item.objection_date,
          status: item.OBJECTION_STATUS || "Pending",
          project: item.project_name || `Project ${item.project_id || "Unknown"}`,
          address: item.objector_address,
          phone: item.objector_contact,
          description: item.objection_summary,
          objectionDetails: item.objection_details || "",
          caseNo: item.case_no,
          affectedSurveyNo: item.affected_survey_no || "",
          hearing_date: item.hearing_date,
          village_name: item.village_name || item.village || "", 
          fullData: item,
        }));

        const privateObjs = safePrivateData.map((item, index) => ({
          id: `P${item.pobjection_id || index}`,
          type: "Private",
          name: item.owner_name || "Unknown",
          date: item.objection_date,
          status: item.OBJECTION_STATUS || "Pending",
          project: item.project_name || `Project ${item.project_id || "Unknown"}`,
          address: item.owner_address,
          phone: item.owner_contact,
          description: item.objection_summary,
          caseNo: item.case_no,
          affectedSurveyNo: item.affected_survey_no || "",
          hearing_date: item.hearing_date,
          village_name: item.village_name || item.village || "", 
          fullData: item,
        }));

        const combined = [...general, ...privateObjs];
        setObjections(combined);

        if (combined.length === 0) {
          setNotification({ show: true, type: "info", message: "Previous Step is not Completed" });
        } else {
          setNotification({ show: true, type: "success", message: `Loaded ${combined.length} objections successfully` });
        }
      } catch (error) {
        setNotification({ show: true, type: "error", message: "Failed to load objections. Please try again." });
        setObjections([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllObjections();
  }, [currentProjectId]);

  const getDownloadData = () =>
    sortedObjections.map((obj, index) => ({
      serialNumber: index + 1,
      objectorName: obj.name || "N/A",
      address: obj.address || "N/A",
      objectionDate: obj.date ? new Date(obj.date).toLocaleDateString("en-GB") : "N/A",
      caseNumber: obj.affectedSurveyNo || obj.caseNo || "N/A",
      objectionDetails: obj.objectionDetails || obj.description || "N/A",
      hearingDate: obj.hearing_date ? new Date(obj.hearing_date).toLocaleDateString("en-GB") : "N/A",
      officerDecision: obj.fullData?.hearing_outcome || "N/A",
    }));

  const getDownloadColumns = () => [
    { header: "अनुक्रमांक", key: "serialNumber", width: 5 },
    { header: "अर्जदार यांचे नाव", key: "objectorName", width: 40 },
    { header: "पत्ता", key: "address", width: 25 },
    { header: "हरकत अर्ज दिनांक", key: "objectionDate", width: 15 },
    { header: "गट क्रमांक", key: "caseNumber", width: 5 },
    { header: "हरकतीचा तपशील", key: "objectionDetails", width: 40 },
    { header: "सुनावणी दिनांक", key: "hearingDate", width: 10 },
    { header: "भूसंपादन अधिकारी यांचा निर्णय", key: "officerDecision", width: 30 },
  ];

  const handlePrint = () => {
    const selectedProposal = JSON.parse(localStorage.getItem("selectedProposal") || "{}");
    const proposalName = selectedProposal.section_11_bhusampadan_no || "";
    const columns = ["अनुक्रमांक", "अर्जदार यांचे नाव", "पत्ता", "हरकत अर्ज दिनांक", "गट क्रमांक", "हरकतीचा तपशील", "सुनावणी दिनांक", "भूसंपादन अधिकारी यांचा निर्णय"];
    const rows = getDownloadData().map((row, index) => [index + 1, row.objectorName || "", row.address || "", row.objectionDate || "", row.caseNumber || "", row.objectionDetails || "", row.hearingDate || "", row.officerDecision || ""]);

    const tableHtml = `
    <html>
      <head>
        <meta charset="UTF-8" />
          <style>
          @page { size: A4 portrait; margin-top: 0.19in; margin-bottom: 0.25in; margin-left: 0.81in; margin-right: 0.5in; }
          body { font-family: 'DVOT-Surekh', 'Mangal', 'Arial', sans-serif; }
          h2, h3 { text-align: center; margin: 5px 0; }
          table { border-collapse: collapse; width: 100%; margin-top: 15px; }
          th, td { border: 1px solid #000; padding: 6px; text-align: center; }
        </style>
      </head>
      <body>
        <h2>कलम 11(1) खालील प्रारंभिक अधिसूचनेवर हरकत दाखल करणाऱ्यांची यादी</h2>
            ${proposalName ? `<h3 style="text-align:center; margin-top:6px; font-weight:600;">भूसंपादन प्रकरण क्र.: ${proposalName}</h3>` : ""}
        <table>
          <thead><tr>${columns.map((c) => `<th>${c}</th>`).join("")}</tr></thead>
          <tbody>${rows.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`).join("")}</tbody>
        </table>
      </body>
    </html>
  `;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(tableHtml);
    printWindow.document.close();
    printWindow.print();
  };

  const handleRaiseObjection = (village) => {
    localStorage.setItem("selectedVillageForObjection", JSON.stringify(village));
    setSubComponent("GeneralObjectionForm");
  };

  const handleSection15 = () => setSubComponent("Section15");
  const handleSection15Notice = () => setSubComponent("Section15Notice");

  useEffect(() => {
    function handleClickOutside(event) {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) setIsDownloadOpen(false);
      if (filterRef.current && !filterRef.current.contains(event.target)) setIsFilterOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => setNotification({ ...notification, show: false }), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const filteredObjections = objections.filter((obj) => {
    const matchesSearch = obj.name.toLowerCase().includes(searchTerm.toLowerCase()) || obj.project.toLowerCase().includes(searchTerm.toLowerCase()) || obj.id.toString().includes(searchTerm.toLowerCase()) || (obj.description && obj.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === "" || obj.status === filterStatus;
    const matchesTab = activeTab === "all" || (activeTab === "general" && obj.type === "General") || (activeTab === "private" && obj.type === "Private");
    return matchesSearch && matchesStatus && matchesTab;
  });

  const sortedObjections = React.useMemo(() => {
    let sortableItems = [...filteredObjections];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === "ascending" ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [filteredObjections, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") direction = "descending";
    setSortConfig({ key, direction });
  };

  const getSortDirectionIcon = (name) => {
    if (sortConfig.key !== name) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
      );
    }
    return sortConfig.direction === "ascending" ? (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
    ) : (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
    );
  };

  const fetchBackToLAODate = async () => {
    try {
      if (!currentProjectId || !currentProposalId) return;
      const response = await apiService.getfetchLAOPublishButton(currentProjectId, currentProposalId);
      if (response.status === 200 && response.data.length > 0) {
        const fetchedStartDate = response.data[0].cre_usr_dt; 
        const formattedDate = new Date(fetchedStartDate).toISOString().split("T")[0];
        setStartDate(formattedDate);
      }
    } catch (error) {}
  };

  useEffect(() => {
    fetchBackToLAODate();
  }, [currentProjectId, currentProposalId]);

  let isObjectionAllowed = true;
  let expiryDate = null;

  if (startDate) {
    expiryDate = new Date(startDate);
    expiryDate.setDate(expiryDate.getDate() + 60); 
    isObjectionAllowed = new Date() <= expiryDate;
  }

  const renderObjectionTables = () => {
    if (!villages || villages.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-md p-8 text-center border border-gray-200">
          <p className="text-gray-500">Loading villages or no villages found for this proposal...</p>
        </div>
      );
    }

    return villages.map((village) => {
      const villageObjections = sortedObjections.filter(
        (obj) => obj.village_name === village.village_name
      );

      return (
        <div key={village.dtv_id} className="mb-8 overflow-hidden rounded-xl border border-gray-200 shadow-lg bg-white">
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div>
              <h3 className="text-lg font-bold text-gray-800">
              {translations[language].village}{village.village_name}
              </h3>
              <p className="text-sm text-gray-500 font-medium">
                {village.taluka_name}, {village.dist_name}
              </p>
            </div>
            
            {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
              <button
                onClick={() => handleRaiseObjection(village)}
                disabled={userRole !== ROLES.DATA_ENTRY && (!startDate || !isObjectionAllowed)}
                className={`flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg transition-all shadow-md ${
                  userRole !== ROLES.DATA_ENTRY && (!startDate || !isObjectionAllowed)
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-green-600 text-white hover:bg-green-700 hover:shadow-lg"
                }`}
                title={
                  userRole === ROLES.DATA_ENTRY ? "" : 
                  !startDate ? "Proposal is not published yet." : 
                  !isObjectionAllowed ? "Objection period has expired." : ""
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                {translations[language]?.RaiseObjection || "Raise Objection"}
              </button>
            )}
          </div>

          {villageObjections.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {userRole === ROLES.LAO && (
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {translations[language].Actions}
                      </th>
                    )}
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => requestSort("date")}>
                      <div className="flex items-center space-x-1">
                        <span>{translations[language].Date}</span>
                        {getSortDirectionIcon("date")}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => requestSort("name")}>
                      <div className="flex items-center space-x-1">
                        <span>{translations[language].OBJECTOR}</span>
                        {getSortDirectionIcon("name")}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => requestSort("status")}>
                      <div className="flex items-center space-x-1">
                        <span>{translations[language].Status}</span>
                        {getSortDirectionIcon("status")}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {villageObjections.map((obj) => (
                    <tr key={obj.id} className="hover:bg-gray-50 transition-colors duration-150">
                      {userRole === ROLES.LAO && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            className="bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-1 rounded-md transition duration-150 flex items-center shadow"
                            onClick={async () => {
                              setLoadingId(obj.id);
                              try { await handleViewDetails(obj.type, obj); } 
                              finally { setLoadingId(null); }
                            }}
                            disabled={loadingId === obj.id}
                          >
                            {loadingId === obj.id ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
                            {translations[language].View}
                          </button>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(obj.date).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                            {obj.name.charAt(0)}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{obj.name}</div>
                            <div className="text-sm text-gray-500">{obj.phone}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingStatusId === obj.id && userRole === 3 ? (
                          <div className="flex flex-col space-y-2">
                            <select className="border rounded px-2 py-1 text-sm" onChange={(e) => {}}>
                              <option value="">Select decision</option>
                              <option value="Completed">Mark Completed</option>
                              <option value="Reject">Mark Incomplete</option>
                            </select>
                            <input type="date" className="border rounded px-2 py-1 text-sm" value={selectedDecisionDate} onChange={(e) => setSelectedDecisionDate(e.target.value)} />
                            <div className="flex space-x-2">
                              <button disabled={!selectedDecisionDate || savingId === obj.id} className="bg-green-500 text-white px-3 py-1 text-xs rounded flex items-center"
                                onClick={async () => {
                                  setSavingId(obj.id);
                                  try {
                                    const numericId = obj.id.replace(/^G|P/, "");
                                    await apiService.patchUpdateGeneralObjection(numericId, { OBJECTION_STATUS: "Completed", hearing_date: selectedDecisionDate });
                                    const updated = objections.map((o) => {
                                      const oNumeric = o.id.replace(/^G|P/, "");
                                      return oNumeric === numericId ? { ...o, status: "Completed", hearing_date: selectedDecisionDate } : o;
                                    });
                                    setObjections(updated);
                                    setEditingStatusId(null);
                                    setNotification({ show: true, type: "success", message: "Status updated successfully" });
                                  } catch (err) {
                                    setNotification({ show: true, type: "error", message: "Failed to update status" });
                                  } finally {
                                    setSavingId(null);
                                  }
                                }}
                              >
                                {savingId === obj.id ? <><RefreshCw className="h-3 w-3 mr-1 animate-spin" /> Saving...</> : "Save"}
                              </button>
                              <button className="bg-gray-300 text-gray-800 px-3 py-1 text-xs rounded" onClick={() => { setSelectedDecisionDate(""); setEditingStatusId(null); }}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <span onClick={() => { if (obj.status === "Pending" && userRole === 3) setEditingStatusId(obj.id); }}
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              obj.status === "Pending" ? (userRole === 3 ? "bg-yellow-100 text-yellow-800 cursor-pointer hover:bg-yellow-200" : "bg-yellow-100 text-yellow-800") :
                              obj.status === "Completed" ? "bg-green-100 text-green-800" :
                              obj.status === "Reject" || obj.status === "Rejected" ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {obj.status === "Completed" && obj.hearing_date ? `${obj.status} (${new Date(obj.hearing_date).toLocaleDateString("en-GB")})` : obj.status}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500 bg-gray-50">
              No objections have been filed for this village yet.
            </div>
          )}
        </div>
      );
    });
  };

  const renderListView = () => {
    return (
      <>
        {notification.show && (
          <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg max-w-md z-50 flex items-center ${notification.type === "success" ? "bg-green-100 border-l-4 border-green-500 text-green-700" : notification.type === "error" ? "bg-red-100 border-l-4 border-red-500 text-red-700" : "bg-blue-100 border-l-4 border-blue-500 text-blue-700"} transform transition-transform duration-300 animate-slideInRight`}>
            <div className="mr-3">
              {notification.type === "success" ? (
                <svg className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
              ) : notification.type === "error" ? (
                <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              ) : (
                <svg className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              )}
            </div>
            <div>
              <p className="font-bold">{notification.type === "success" ? "Success!" : notification.type === "error" ? "Error!" : "Info"}</p>
              <p>{notification.message}</p>
            </div>
            <button onClick={() => setNotification({ ...notification, show: false })} className="ml-auto text-gray-600 hover:text-gray-800">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        )}

        <div className="bg-white rounded-b-lg shadow-lg">
          <div className="p-4">
            <div className="mb-6 animate-slideInLeft" style={{ animationDelay: "200ms" }}>
             {
            //  pageActive ? 
             <div className="flex flex-col lg:flex-row gap-4 items-center">
               

               

                <button onClick={handleSection15} className="flex items-center justify-center px-4 py-2 text-sm font-medium h-10 whitespace-nowrap text-white rounded-lg shadow-sm border border-transparent bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200">
                  <FileText className="h-4 w-4 mr-2" />
                  {translations[language].Section15}
                </button>
                 {(userRole === ROLES.SAMANVAY || userRole === ROLES.COLLECTOR) && (
                  <button onClick={handleCoveringLetterClick} className="flex items-center justify-center px-4 py-2 text-sm font-medium h-10 whitespace-nowrap bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-sm">
                    कलम 15 मान्यता
                  </button>
                )}
                 <button onClick={handleSection15Notice} className="flex items-center justify-center px-4 py-2 text-sm font-medium h-10 whitespace-nowrap text-white rounded-lg shadow-sm border border-transparent bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200">
                  <FileText className="h-4 w-4 mr-2" />
                  {translations[language].Section15Notice}
                </button>

                <button onClick={handlePrint} className="flex items-center justify-center px-4 py-2 text-sm font-medium h-10 whitespace-nowrap border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 shadow-sm transition-colors">
                  <Download className="mr-2 h-4 w-4" />
                  {translations[language].Download}
                </button>

                <div className="flex flex-wrap items-center gap-3 p-2 lg:ml-auto border border-purple-200 bg-purple-50 rounded-xl shadow-sm min-w-max">
                  {userRole === ROLES.LAO && (
                    <button type="button" onClick={() => { if (isSamanvay) { showConfirm(translations[language]?.confirmResubmission, translations[language]?.resubmissionWarning, () => setActiveModal("SAMANVAY"), () => {}, "danger"); } else { setActiveModal("SAMANVAY"); } }} className={`flex items-center justify-center px-4 py-2 text-sm font-medium h-10 whitespace-nowrap rounded-lg transition-colors shadow-sm ${isSamanvay ? "bg-red-600 text-white hover:bg-red-700" : "bg-purple-600 text-white hover:bg-purple-700"}`} title={isSamanvay ? translations[language]?.AlreadySentToSamanvayOfficerClickToResend : translations[language]?.SendToSamanvayOfficer}>
                      <Send className="mr-2 h-4 w-4" />
                      {translations[language]?.SendToSamanvayOfficer}
                    </button>
                  )}
                  {userRole === ROLES.COLLECTOR && (
                    <button type="button" onClick={() => { if (isSamanvayFinal) { showConfirm(translations[language]?.confirmResubmission, translations[language]?.resubmissionWarning, () => setActiveModal("SAMANVAY"), () => {}, "danger"); } else { setActiveModal("SAMANVAY"); } }} className={`flex items-center justify-center px-4 py-2 text-sm font-medium h-10 whitespace-nowrap rounded-lg transition-colors shadow-sm ${isSamanvayFinal ? "bg-red-600 text-white hover:bg-red-700" : "bg-purple-600 text-white hover:bg-purple-700"}`} title={isSamanvayFinal ? translations[language]?.AlreadySentToSamanvayOfficerClickToResend : translations[language]?.SendToSamanvayOfficer}>
                      <Send className="mr-2 h-4 w-4" />
                      {translations[language]?.SendToSamanvayOfficer}
                    </button>
                  )}

                  {userRole === ROLES.SAMANVAY && (
                    <button type="button" onClick={() => { if (isCollector) { showConfirm(translations[language]?.confirmResubmission, translations[language]?.resubmissionWarning, () => setActiveModal("COLLECTOR"), () => {}, "danger"); } else { setActiveModal("COLLECTOR"); } }} className={`flex items-center justify-center px-4 py-2 text-sm font-medium h-10 whitespace-nowrap rounded-lg transition-colors shadow-sm ${isCollector ? "bg-red-600 text-white hover:bg-red-700" : "bg-purple-600 text-white hover:bg-purple-700"}`} title={isCollector ? translations[language]?.SendToCollectorAlreadySentClickToResend : translations[language]?.SendToCollector}>
                      <Send className="mr-2 h-4 w-4" />
                      {translations[language]?.SendToCollector}
                    </button>
                  )}

                  {userRole === ROLES.SAMANVAY && (
                    <button type="button" onClick={() => { if (isLAO) { showConfirm(translations[language]?.confirmResubmission, translations[language]?.resubmissionWarning, () => setActiveModal("LAO"), () => {}, "danger"); } else { setActiveModal("LAO"); } }} className={`flex items-center justify-center px-4 py-2 text-sm font-medium h-10 whitespace-nowrap rounded-lg transition-colors shadow-sm ${isLAO ? "bg-red-600 text-white hover:bg-red-700" : "bg-purple-600 text-white hover:bg-purple-700"}`} title={isLAO ? translations[language]?.AlreadySentBackToLAOClickToResend : translations[language]?.sendToLAO}>
                      <Send className="mr-2 h-4 w-4" />
                      {translations[language]?.sendToLAO}
                    </button>
                  )}
                </div>
              </div>
              //  : "Previous Step is not Completed"
  }
            </div>

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
              <div className="text-sm text-gray-600 mb-2 md:mb-0">
                <span className="font-medium">
                  {translations[language].Showing} {sortedObjections.length} {translations[language].Of} {objections.length} {translations[language].Objections}
                </span>
                {(searchTerm || filterStatus || activeTab !== "all") && (
                  <button className="ml-2 text-blue-600 hover:text-blue-800 underline" onClick={() => { setSearchTerm(""); setFilterStatus(""); setActiveTab("all"); }}>
                    {translations[language].Clear_all_filters}
                  </button>
                )}
              </div>
              <div className="flex gap-4">
                <div className="flex items-center">
                  <span className="inline-block w-3 h-3 bg-yellow-300 rounded-full mr-2"></span>
                  <span className="text-sm">{translations[language].pending}: {objections.filter((o) => o.status === "Pending").length}</span>
                </div>
                <div className="flex items-center">
                  <span className="inline-block w-3 h-3 bg-green-400 rounded-full mr-2"></span>
                  <span className="text-sm">{translations[language].Completed}: {objections.filter((o) => o.status === "Completed").length}</span>
                </div>
              </div>
            </div>

            <div className="mt-8">
              {renderObjectionTables()}
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderOtherScreens = () => {
    return (
      <div className="p-6 border rounded-lg bg-white shadow-lg">
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <div className="mb-4">
            <span className="text-sm text-gray-500">{translations[language].OBJECTOR}:</span>
            <h4 className="text-lg font-medium">{selectedObjection ? selectedObjection.name : "New Entry"}</h4>
          </div>
          {selectedObjection && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <span className="text-sm text-gray-500">{translations[language].PROJECT}:</span>
                <p>{selectedObjection.project}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">{translations[language].STATUS}:</span>
                <p className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${selectedObjection.status === "Pending" ? "bg-yellow-100 text-yellow-800" : selectedObjection.status === "Scheduled" ? "bg-blue-100 text-blue-800" : selectedObjection.status === "Completed" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                  {selectedObjection.status}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">{translations[language].address}:</span>
                <p>{selectedObjection.address}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">{translations[language].Phone}:</span>
                <p>{selectedObjection.phone}</p>
              </div>
              <div className="md:col-span-2">
                <span className="text-sm text-gray-500">{translations[language].description}:</span>
                <p>{selectedObjection.description}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (shouldRestoreModal && modalParams) {
      setActiveModal("COLLECTOR");
      setRestoredModalData({
        user: modalParams.selectedUser || "",
        remarks: modalParams.remarks || "Approved"
      });
      if (setShouldRestoreModal) setShouldRestoreModal(false);
    }
  }, [shouldRestoreModal, modalParams, setShouldRestoreModal]);

  const handleCoveringLetterClick = () => {
    if (typeof onNavigateWithModal !== "function") return;
    const modalState = { selectedUser: "", userId: "", remarks: "Approved", selectedDocumentName: null, timestamp: new Date().getTime() };
    setActiveModal(null);
    onNavigateWithModal("CoveringLettersSection15", modalState);
  };

  if (loadingRecords || isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex justify-center items-center h-80">
          <div className="text-center animate-float">
            <div className="inline-flex relative">
              <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-transparent animate-spin"></div>
              <div className="w-16 h-16 rounded-full border-4 border-purple-500 border-b-transparent border-r-transparent animate-spin absolute inset-0" style={{ animationDuration: "1.5s", animationDirection: "reverse" }}></div>
              <div className="absolute inset-0 flex items-center justify-center"><FileText className="h-6 w-6 text-purple-400 animate-pulse" /></div>
            </div>
            <p className="text-gray-400 mt-4">हरकत यादी लोड होत आहे...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 relative">
      {activeScreen === "list" ? renderListView() : renderOtherScreens()}
      <DownloadModal isOpen={isDownloadOpen} onClose={() => setIsDownloadOpen(false)} title={translations[language].objectionList} data={getDownloadData()} columns={getDownloadColumns()} isLoading={isLoading} setIsLoading={setIsLoading} screenName="EnhancedObjectionList" />
      {renderDetailsModal()}
      {renderImageModal()}

      <SendTrackingModal isOpen={showTrackingModal} onClose={() => setShowTrackingModal(false)} role={trackingRole} projectId={currentProjectId} proposalId={currentProposalId} translations={translations} language={language} />

      {/* Render the Single Reusable Modal */}
      {activeModal && (
        <DocumentForwardModal
          isOpen={!!activeModal}
          onClose={() => {
            setActiveModal(null);
            if (activeModal === 'SAMANVAY') resetSignatureState();
          }}
          targetRole={activeModal}
          currentUserRole={userRole}
          ROLES={ROLES}
          translations={translations}
          language={language}
          onSubmit={handleUnifiedSubmit}
          isSubmitting={isSubmittingTask}
          initialUser={restoredModalData.user}
          initialRemarks={restoredModalData.remarks}
          
          {...getModalConfig()} 
          
          uploadStatus={uploadStatus}
          selectedSignDocument={selectedSignDocument}
          setShowSignaturePad={setShowSignaturePad}
          setPdfForSigning={setPdfForSigning}
          handleViewDocument={handleViewDocument}
        />
      )}

      {/* Signature Pad Component - Rendered at root level when triggered */}
      {showSignaturePad && pdfForSigning && (
        <SignaturePad
          pdfFile={pdfForSigning}
          onSign={(signaturesArray) => {
            setShowSignaturePad(false);
            signDocument(pdfForSigning, signaturesArray).then(() => {
              setPdfForSigning(null);
            });
          }}
          onCancel={() => {
            setShowSignaturePad(false);
            setPdfForSigning(null);
          }}
        />
      )}

      {showDocumentViewer && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-5xl h-[95vh] shadow-2xl overflow-hidden border border-gray-200">
            <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="text-2xl font-bold text-gray-800">Document Preview</h3>
              <button onClick={closeDocumentViewer} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-500 text-xl font-bold">×</button>
            </div>
            <div className="h-[calc(95vh)] flex flex-col">
              <div className="flex-1 bg-gray-50 overflow-hidden shadow-inner border border-gray-200 flex items-center justify-center">
                {isPDFToView ? (
                  <iframe src={fileUrlToView} className="w-full h-full border-0 bg-white shadow-lg" title="PDF Preview"></iframe>
                ) : (
                  <img src={fileUrlToView} alt="File" className="w-full h-full object-contain shadow-lg" />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showAlertModal && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`bg-white rounded-lg shadow-lg border-l-4 p-4 max-w-sm ${alertConfig.type === "error" ? "border-red-500" : "border-green-500"}`}>
            <div className="flex items-start">
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-gray-900">{alertConfig.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{alertConfig.message}</p>
              </div>
              <button onClick={() => setShowAlertModal(false)} className="ml-4 text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">{confirmConfig.title}</h3>
              <p className="text-gray-600 text-sm mb-6 text-center">{confirmConfig.message}</p>
              <div className="flex gap-3">
                <button onClick={() => { setShowConfirmModal(false); if (confirmConfig.onCancel) confirmConfig.onCancel(); }} className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">{translations[language]?.Cancel}</button>
                <button onClick={() => { setShowConfirmModal(false); if (confirmConfig.onConfirm) confirmConfig.onConfirm(); }} className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700">{translations[language]?.Confirm}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}